import React, { useState, useEffect } from 'react';
import CommonModalLayout from '../Layouts/CommonModalLayout';
import { buttonStyles, inputStyles } from '../../styles/commonStyles';
import { useTranslation } from 'react-i18next';
import { createCategory, getCategoryGroupsByUserId } from '../../api/categories';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient, useQuery } from 'react-query';
import { Select } from '../ui/select';

const GROUP_KEY = 'lastSelectedGroupId';

const CreateCategoryModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [categoryName, setCategoryName] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('none');
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const userId = useSelector(state => state.auth.user?.id);

    // Получаем группы пользователя
    const { data: groupsData } = useQuery(
        ['categoryGroups', userId],
        () => getCategoryGroupsByUserId(userId),
        { enabled: !!userId }
    );
    const groupOptions = [
        { value: 'none', label: t('categories.withoutGroup', 'Без группы') },
        ...(groupsData ? groupsData.map(group => ({ value: String(group.id), label: group.name })) : [])
    ];

    // Сброс формы при открытии/закрытии, дефолт — последняя выбранная группа из sessionStorage
    useEffect(() => {
        if (isOpen) {
            setCategoryName('');
            const lastGroup = sessionStorage.getItem(GROUP_KEY);
            setSelectedGroup(lastGroup || 'none');
        }
    }, [isOpen]);

    const handleCreate = async () => {
        try {
            const groupIdToSend = selectedGroup === 'none' ? null : selectedGroup;
            const response = await createCategory({ name: categoryName, group_id: groupIdToSend });
            queryClient.invalidateQueries(['categories', userId]);
            queryClient.invalidateQueries(['groupedCategories', userId]);
            // Сохраняем выбранную группу в sessionStorage
            sessionStorage.setItem(GROUP_KEY, selectedGroup);
            handleClose();
        } catch (error) {
            console.error('Error creating category:', error);
            console.error('Error details:', error.response?.data);
            alert(`Error: ${error.message}`);
        }
    };

    const handleClose = () => {
        setCategoryName('');
        // Не сбрасываем selectedGroup, чтобы дефолт был актуальным для следующего открытия
        onClose();
    };

    return (
        <CommonModalLayout
            isOpen={isOpen}
            onClose={handleClose}
            title={t('categories.create')}
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('categories.name')}
                    </label>
                    <input
                        type="text"
                        id="categoryName"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        className={inputStyles}
                        placeholder={t('categories.namePlaceholder')}
                    />
                </div>
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('categories.group', 'Группа')}
                    </label>
                    <Select
                        options={groupOptions}
                        value={selectedGroup}
                        onValueChange={setSelectedGroup}
                        className={inputStyles}
                        placeholder={t('categories.selectGroup', 'Без группы')}
                    />
                </div>
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={handleClose}
                        className={buttonStyles}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleCreate}
                        className={buttonStyles}
                        disabled={!categoryName.trim()}
                    >
                        {t('common.create')}
                    </button>
                </div>
            </div>
        </CommonModalLayout>
    );
};

export default CreateCategoryModal; 